import styled, { css } from "styled-components";

const OrderGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 16px;

  @media (max-width: 1800px) {
    grid-template-columns: repeat(5, 1fr);
  }

  @media (max-width: 1400px) {
    grid-template-columns: repeat(4, 1fr);
  }

  @media (max-width: 1100px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (max-width: 800px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

const AdminOrderCard = styled.article`
  position: relative;
  display: flex;
  flex-direction: column;
  min-height: 100%;
  padding: 36px 14px 14px;
  background: #fff;
  border: 1px solid #e8ecf0;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.06);
  cursor: pointer;
  overflow: hidden;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:focus-visible {
    outline: 2px solid #1594db;
    outline-offset: 2px;
  }
`;

const CardBadge = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  padding: 4px 10px;
  border-bottom-right-radius: 10px;
  background: linear-gradient(135deg, #1594db 0%, #0f6fa8 100%);
  color: #fff;
  font-size: 10px;
  font-family: "Inter600";
  letter-spacing: 0.06em;
  text-transform: uppercase;
`;

const PaymentIcon = styled.div`
  position: absolute;
  top: 8px;
  right: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: ${({ $succeeded }) => ($succeeded ? "#e8f4fc" : "#fff4e5")};
  color: ${({ $succeeded }) => ($succeeded ? "#1594db" : "#e67e22")};

  svg {
    font-size: 16px;
  }
`;

const OrderNumber = styled.h3`
  margin: 0 0 12px;
  padding-right: 24px;
  font-size: 13px;
  font-family: "Inter600";
  color: #374151;
  line-height: 1.3;
  word-break: break-word;
`;

const MetaList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
`;

const MetaRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
`;

const MetaLabel = styled.span`
  font-size: 9px;
  font-family: "Inter600";
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #9ca3af;
`;

const MetaValue = styled.span`
  font-size: 11px;
  font-family: "Inter500";
  color: #4b5563;
  line-height: 1.35;
  word-break: break-word;

  ${({ $accent }) =>
    $accent &&
    css`
      color: #1594db;
    `}

  ${({ $truncate }) =>
    $truncate &&
    css`
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
    `}
`;

const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  align-self: flex-start;
  max-width: 100%;
  padding: 3px 8px;
  border-radius: 999px;
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
  font-size: 10px;
  font-family: "Inter600";
  color: #4b5563;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const StatusBadge = styled.div`
  margin-top: 2px;
  padding: 6px 8px;
  border-radius: 8px;
  text-align: center;
  font-size: 11px;
  font-family: "Inter600";
  text-transform: capitalize;
  letter-spacing: 0.02em;

  ${({ $tone }) => {
    switch ($tone) {
      case "green":
        return css`
          color: #047857;
          background: #ecfdf5;
          border: 1px dashed #6ee7b7;
        `;
      case "red":
        return css`
          color: #b91c1c;
          background: #fef2f2;
          border: 1px dashed #fca5a5;
        `;
      case "review":
        return css`
          color: #b45309;
          background: #fffbeb;
          border: 1px dashed #fcd34d;
        `;
      default:
        return css`
          color: #c2410c;
          background: #fff7ed;
          border: 1px dashed #fdba74;
        `;
    }
  }}
`;

const CardFooter = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 8px;
  margin-top: 12px;
  padding-top: 10px;
  border-top: 1px solid #f0f2f5;
`;

const FooterLeft = styled.div`
  display: flex;
  align-items: center;

  img {
    width: 22px;
    height: 22px;
    border-radius: 4px;
    object-fit: contain;
  }
`;

const FooterRight = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
  min-width: 0;
`;

const TotalPrice = styled.span`
  font-size: 13px;
  font-family: "Inter600";
  color: #111827;
  white-space: nowrap;
`;

const FooterMeta = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 6px;
  border-radius: 999px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  font-size: 9px;
  font-family: "Inter600";
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  white-space: nowrap;

  img {
    width: 14px;
    height: 10px;
    border-radius: 2px;
    object-fit: cover;
  }
`;

const OrdersSection = styled.div`
  padding: 8px 0 0;
`;

const SectionHeading = styled.div`
  font-size: 18px;
  font-family: "Inter600";
  color: #1594db;
  margin-bottom: 16px;
`;

const DialogBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 4px 0 8px;
`;

const DialogMetaBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
`;

const DialogMetaChip = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 5px 10px;
  border-radius: 999px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  font-size: 12px;
  font-family: "Inter500";
  color: #475569;
`;

const DialogMetaChipAccent = styled(DialogMetaChip)`
  background: #eff6ff;
  border-color: #bfdbfe;
  color: #1d4ed8;
  text-transform: capitalize;
`;

const DialogSection = styled.section`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const DialogSectionTitle = styled.h3`
  margin: 0;
  font-size: 12px;
  font-family: "Inter600";
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #64748b;
`;

const LineItemsTable = styled.div`
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  overflow: hidden;
  background: #fff;
`;

const LineItemsHeader = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.6fr) 120px 72px 96px 88px;
  gap: 12px;
  padding: 10px 16px;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
  font-size: 11px;
  font-family: "Inter600";
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #64748b;

  @media (max-width: 640px) {
    display: none;
  }
`;

const LineItemRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.6fr) 120px 72px 96px 88px;
  gap: 12px;
  align-items: center;
  padding: 14px 16px;
  border-bottom: 1px solid #f1f5f9;

  &:last-child {
    border-bottom: none;
  }

  @media (max-width: 640px) {
    grid-template-columns: 56px minmax(0, 1fr) auto;
    grid-template-areas:
      "image details actions"
      "image price actions";
    gap: 8px 12px;
  }
`;

const LineItemArtwork = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
`;

const LineItemImage = styled.img`
  width: 56px;
  height: 56px;
  flex-shrink: 0;
  object-fit: contain;
  border-radius: 8px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
`;

const LineItemDetails = styled.div`
  min-width: 0;

  @media (max-width: 640px) {
    grid-area: details;
  }
`;

const LineItemName = styled.p`
  margin: 0;
  font-size: 14px;
  font-family: "Inter600";
  color: #0f172a;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const LineItemNote = styled.p`
  margin: 4px 0 0;
  font-size: 12px;
  color: #64748b;
`;

const LineItemSize = styled.span`
  font-size: 13px;
  color: #334155;

  @media (max-width: 640px) {
    display: none;
  }
`;

const LineItemQty = styled.span`
  font-size: 13px;
  font-family: "Inter600";
  color: #334155;

  @media (max-width: 640px) {
    display: none;
  }
`;

const LineItemPrice = styled.span`
  font-size: 14px;
  font-family: "Inter600";
  color: #0f172a;
  text-align: right;
  white-space: nowrap;

  @media (max-width: 640px) {
    grid-area: price;
    text-align: left;
  }
`;

const LineItemActions = styled.div`
  display: flex;
  justify-content: flex-end;

  @media (max-width: 640px) {
    grid-area: actions;
    align-self: start;
  }
`;

const DownloadArtworkButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 6px 10px;
  border: 1px solid #bfdbfe;
  border-radius: 8px;
  background: #eff6ff;
  color: #1d4ed8;
  font-size: 11px;
  font-family: "Inter600";
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.15s ease, border-color 0.15s ease;

  &:hover:not(:disabled) {
    background: #dbeafe;
    border-color: #93c5fd;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  svg {
    font-size: 14px;
  }
`;

const DetailsColumns = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const DetailPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 16px;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  background: #f8fafc;
  min-height: 100%;
`;

const DetailPanelTitle = styled.h4`
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 0;
  font-size: 12px;
  font-family: "Inter600";
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #64748b;
`;

const DetailLine = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  font-size: 13px;
  color: #334155;
`;

const DetailLineLabel = styled.span`
  color: #64748b;
`;

const DetailLineValue = styled.span`
  font-family: "Inter500";
  text-align: right;
`;

const DetailTotalLine = styled(DetailLine)`
  margin-top: 4px;
  padding-top: 12px;
  border-top: 1px solid #e2e8f0;
  font-size: 15px;
  font-family: "Inter600";
  color: #0f172a;
`;

const DetailText = styled.p`
  margin: 0;
  font-size: 13px;
  color: #334155;
  line-height: 1.55;
`;

const DetailTextMuted = styled(DetailText)`
  color: #64748b;
`;

const ReceiptLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 4px;
  font-size: 13px;
  font-family: "Inter500";
  color: #1594db;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const EmptyLineItems = styled.div`
  padding: 24px 16px;
  text-align: center;
  font-size: 13px;
  color: #64748b;
  border: 1px dashed #cbd5e1;
  border-radius: 12px;
  background: #f8fafc;
`;

export {
  OrderGrid,
  AdminOrderCard,
  CardBadge,
  PaymentIcon,
  OrderNumber,
  MetaList,
  MetaRow,
  MetaLabel,
  MetaValue,
  Chip,
  StatusBadge,
  CardFooter,
  FooterLeft,
  FooterRight,
  TotalPrice,
  FooterMeta,
  OrdersSection,
  SectionHeading,
  DialogBody,
  DialogMetaBar,
  DialogMetaChip,
  DialogMetaChipAccent,
  DialogSection,
  DialogSectionTitle,
  LineItemsTable,
  LineItemsHeader,
  LineItemRow,
  LineItemArtwork,
  LineItemImage,
  LineItemDetails,
  LineItemName,
  LineItemNote,
  LineItemSize,
  LineItemQty,
  LineItemPrice,
  LineItemActions,
  DownloadArtworkButton,
  DetailsColumns,
  DetailPanel,
  DetailPanelTitle,
  DetailLine,
  DetailLineLabel,
  DetailLineValue,
  DetailTotalLine,
  DetailText,
  DetailTextMuted,
  ReceiptLink,
  EmptyLineItems,
};
